import { cva, type VariantProps } from 'class-variance-authority'
import { useContext, type ComponentPropsWithoutRef, type CSSProperties, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { LOGO_WELL_CLASS } from '../../logo/nested/well'
import { CellContext } from './context'

const UNREAD_DOT_PX = 8
const UNREAD_CUTOUT_PX = 2
const UNREAD_MASK_RADIUS_PX = UNREAD_DOT_PX / 2 + UNREAD_CUTOUT_PX
const UNREAD_DOT_CENTER_PX = UNREAD_DOT_PX / 2 - 1

const UNREAD_CUTOUT_MASK = `radial-gradient(circle ${UNREAD_MASK_RADIUS_PX}px at calc(100% - ${UNREAD_DOT_CENTER_PX}px) calc(100% - ${UNREAD_DOT_CENTER_PX}px), transparent ${UNREAD_MASK_RADIUS_PX}px, black ${UNREAD_MASK_RADIUS_PX + 0.5}px)`

/**
 * Punches a bottom-right hole so `UnreadDot` sits in a 2px gap showing the
 * content behind, rather than on top of the icon. Apply to the element the dot
 * decorates and render `UnreadDot` as a sibling — a child would be masked too.
 */
export const unreadDotCutoutStyle: CSSProperties = {
  maskImage: UNREAD_CUTOUT_MASK,
  WebkitMaskImage: UNREAD_CUTOUT_MASK,
}

type IndicatorTone = 'unread' | 'attention'

/** The 8px indicator dot `unreadDotCutoutStyle` cuts the seat for. */
export function UnreadDot({ tone = 'unread' }: { tone?: IndicatorTone }) {
  return (
    <span
      aria-hidden
      className={twMerge(
        'pointer-events-none absolute -bottom-px -right-px size-2 rounded-full',
        tone === 'attention' ? 'bg-yellow' : 'bg-danger',
      )}
    />
  )
}

const cellIconVariants = cva(
  [
    // The logo well's tinted square, at the row's own sizes.
    LOGO_WELL_CLASS,
    '[&_svg:not([class*="text-"])]:text-foreground-secondary [&_svg]:pointer-events-none',
  ],
  {
    variants: {
      size: {
        default: 'size-5 rounded-[6px] [&_:is(svg,img):not([class*="size-"])]:size-3',
        small: 'size-4 rounded-[4px] [&_:is(svg,img):not([class*="size-"])]:size-2.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

type CellIconProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof cellIconVariants> & {
    subIcon?: ReactNode
    /** 8px red unread dot in the bottom-right, seated in a 2px masked cutout. */
    unread?: boolean
    /** Persistent status dot. Attention is yellow, unread is red. */
    indicator?: IndicatorTone
  }

function CellIcon({ className, children, size, subIcon, unread, indicator, style, ...props }: CellIconProps) {
  const context = useContext(CellContext)
  const cellSize = context?.size
  const effectiveSize = size ?? (cellSize === 'compact' ? 'small' : 'default')
  const showIndicator = Boolean(unread || indicator)
  const needsWrapper = Boolean(subIcon || showIndicator)

  const iconElement = (
    <div
      data-slot="cell-icon"
      className={twMerge(cellIconVariants({ size: effectiveSize }), !needsWrapper && 'self-start', className)}
      style={showIndicator ? { ...style, ...unreadDotCutoutStyle } : style}
      {...props}
    >
      {children}
    </div>
  )

  if (!needsWrapper) return iconElement

  return (
    <div className="relative self-start">
      {iconElement}
      {showIndicator && <UnreadDot tone={indicator} />}
      {subIcon ? (
        <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center">{subIcon}</div>
      ) : null}
    </div>
  )
}

export { CellIcon }
